import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Save, Loader2, Hash, RefreshCw, AlertTriangle, CheckCircle2, Pencil, X, Check, ClipboardList, Layers } from 'lucide-react';
import { toast } from 'sonner';

const GlobalDocketSettings = ({ defaultTab }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabParam = searchParams.get('tab') || defaultTab || 'docket';
  const [activeTab, setActiveTab] = useState(activeTabParam === 'booking' ? 'booking' : 'docket');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  }), []);

  // Docket series state
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [inlineEdit, setInlineEdit]     = useState(false);
  const [quickSaving, setQuickSaving]   = useState(false);
  const [inlineForm, setInlineForm]     = useState({ docket_prefix: '', separator: '/', next_number: 1 });
  const [form, setForm] = useState({
    docket_prefix: 'STAVAN',
    separator: '/',
    next_number: 1,
    padding_digits: 6,
    reset_rule: 'FY',
    fy_start_month: 4,
    allow_manual_printed_docket_no: true,
  });

  // Booking series state
  const [bkLoading, setBkLoading]       = useState(false);
  const [bkSaving, setBkSaving]         = useState(false);
  const [bkInlineEdit, setBkInlineEdit] = useState(false);
  const [bkQuickSaving, setBkQuickSaving] = useState(false);
  const [bkInlineForm, setBkInlineForm] = useState({ booking_prefix: 'INQ', separator: '-', next_number: 1 });
  const [bkForm, setBkForm] = useState({
    booking_prefix: 'INQ',
    separator: '-',
    next_number: 1,
    padding_digits: 4,
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setBk = (k, v) => setBkForm(p => ({ ...p, [k]: v }));

  const toUiSep  = v => (v === '' ? 'none' : v);
  const toDbSep  = v => (v === 'none' ? '' : v);

  const preview = `${form.docket_prefix}${toDbSep(form.separator)}${String(form.next_number).padStart(form.padding_digits, '0')}`;
  const bkPreview = `${bkForm.booking_prefix}${toDbSep(bkForm.separator)}${String(bkForm.next_number).padStart(bkForm.padding_digits, '0')}`;

  // Sync tab with URL
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Fetch Docket Series
  const fetchSeries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${backendUrl}/api/settings/docket-series`, { credentials: 'omit', headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to load docket series');
      const data = await res.json();
      setForm({
        docket_prefix:                  data.docket_prefix || 'STAVAN',
        separator:                      data.separator === '' ? 'none' : (data.separator || '/'),
        next_number:                    data.next_number ?? 1,
        padding_digits:                 data.padding_digits ?? 6,
        reset_rule:                     data.reset_rule || 'FY',
        fy_start_month:                 data.fy_start_month ?? 4,
        allow_manual_printed_docket_no: data.allow_manual_printed_docket_no ?? true,
      });
    } catch (e) {
      toast.error(e.message || 'Error loading settings');
    } finally {
      setLoading(false);
    }
  }, [backendUrl, getHeaders]);

  // Fetch Booking Series
  const fetchBookingSeries = useCallback(async () => {
    try {
      setBkLoading(true);
      const res = await fetch(`${backendUrl}/api/settings/booking-series`, { credentials: 'omit', headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to load booking series');
      const data = await res.json();
      setBkForm({
        booking_prefix: data.booking_prefix || 'INQ',
        separator:      data.separator === '' ? 'none' : (data.separator || '-'),
        next_number:    data.next_number ?? 1,
        padding_digits: data.padding_digits ?? 4,
      });
    } catch (e) {
      console.error('Error loading booking series:', e);
    } finally {
      setBkLoading(false);
    }
  }, [backendUrl, getHeaders]);

  useEffect(() => {
    fetchSeries();
    fetchBookingSeries();
  }, [fetchSeries, fetchBookingSeries]);

  // Save Docket Series
  const handleSaveDocket = async () => {
    if (!form.docket_prefix.trim()) { toast.error('Docket prefix is required'); return; }
    if (form.next_number < 1)       { toast.error('Next number must be >= 1'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/settings/docket-series`, {
        method: 'PUT',
        credentials: 'omit',
        headers: getHeaders(),
        body: JSON.stringify({
          docket_prefix:                  form.docket_prefix.toUpperCase().trim(),
          separator:                      toDbSep(form.separator),
          next_number:                    Number(form.next_number),
          padding_digits:                 Number(form.padding_digits),
          reset_rule:                     form.reset_rule,
          fy_start_month:                 Number(form.fy_start_month),
          allow_manual_printed_docket_no: form.allow_manual_printed_docket_no,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast.success('Docket series settings saved');
    } catch (e) {
      toast.error(e.message || 'Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  // Save Booking Series
  const handleSaveBooking = async () => {
    if (!bkForm.booking_prefix.trim()) { toast.error('Booking prefix is required'); return; }
    if (bkForm.next_number < 1)         { toast.error('Next number must be >= 1'); return; }
    setBkSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/settings/booking-series`, {
        method: 'PUT',
        credentials: 'omit',
        headers: getHeaders(),
        body: JSON.stringify({
          booking_prefix: bkForm.booking_prefix.toUpperCase().trim(),
          separator:      toDbSep(bkForm.separator),
          next_number:    Number(bkForm.next_number),
          padding_digits: Number(bkForm.padding_digits),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast.success('Booking number series saved successfully');
    } catch (e) {
      toast.error(e.message || 'Error saving booking settings');
    } finally {
      setBkSaving(false);
    }
  };

  // Quick Docket Save
  const openInlineEdit = () => {
    setInlineForm({ docket_prefix: form.docket_prefix, separator: form.separator, next_number: form.next_number });
    setInlineEdit(true);
  };
  const cancelInlineEdit = () => setInlineEdit(false);
  const handleQuickSave = async () => {
    if (!inlineForm.docket_prefix.trim()) { toast.error('Prefix cannot be empty'); return; }
    setQuickSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/settings/docket-series`, {
        method: 'PUT',
        credentials: 'omit',
        headers: getHeaders(),
        body: JSON.stringify({ docket_prefix: inlineForm.docket_prefix.toUpperCase(), separator: toDbSep(inlineForm.separator), next_number: inlineForm.next_number }),
      });
      if (!res.ok) throw new Error('Save failed');
      const saved = await res.json();
      setForm(f => ({ ...f, docket_prefix: saved.docket_prefix, separator: saved.separator === '' ? 'none' : saved.separator, next_number: saved.next_number }));
      setInlineEdit(false);
      toast.success('Docket series updated');
    } catch (e) {
      toast.error(e.message || 'Update failed');
    } finally {
      setQuickSaving(false);
    }
  };

  // Quick Booking Save
  const openBkInlineEdit = () => {
    setBkInlineForm({ booking_prefix: bkForm.booking_prefix, separator: bkForm.separator, next_number: bkForm.next_number });
    setBkInlineEdit(true);
  };
  const cancelBkInlineEdit = () => setBkInlineEdit(false);
  const handleBkQuickSave = async () => {
    if (!bkInlineForm.booking_prefix.trim()) { toast.error('Prefix cannot be empty'); return; }
    setBkQuickSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/settings/booking-series`, {
        method: 'PUT',
        credentials: 'omit',
        headers: getHeaders(),
        body: JSON.stringify({ booking_prefix: bkInlineForm.booking_prefix.toUpperCase(), separator: toDbSep(bkInlineForm.separator), next_number: bkInlineForm.next_number }),
      });
      if (!res.ok) throw new Error('Save failed');
      const saved = await res.json();
      setBkForm(f => ({ ...f, booking_prefix: saved.booking_prefix, separator: saved.separator === '' ? 'none' : saved.separator, next_number: saved.next_number }));
      setBkInlineEdit(false);
      toast.success('Booking series updated');
    } catch (e) {
      toast.error(e.message || 'Update failed');
    } finally {
      setBkQuickSaving(false);
    }
  };

  const handleInitialize = async () => {
    if (!window.confirm('Scan all existing dockets and sync next counter?')) return;
    setInitializing(true);
    try {
      const res = await fetch(`${backendUrl}/api/settings/docket-series/initialize`, {
        method: 'POST',
        credentials: 'omit',
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error('Sync failed');
      const updated = await res.json();
      setForm(p => ({ ...p, next_number: updated.next_number }));
      toast.success(`Counter synced. Next number: ${updated.next_number}`);
    } catch (e) {
      toast.error(e.message || 'Initialization failed');
    } finally {
      setInitializing(false);
    }
  };

  if (loading && bkLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-7 h-7 animate-spin text-accent-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="global-docket-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-100 rounded-xl flex items-center justify-center">
            {activeTab === 'docket' ? <Hash className="w-5 h-5 text-cyan-600" /> : <ClipboardList className="w-5 h-5 text-accent-blue" />}
          </div>
          <div>
            <h2 className="text-2xl font-bold text-text-dark font-manrope">
              {activeTab === 'docket' ? 'Global Docket Series' : 'Booking Number Series'}
            </h2>
            <p className="text-sm text-text-secondary font-ibm mt-0.5">
              {activeTab === 'docket'
                ? 'Single docket number counter shared across all companies'
                : 'Configure inquiry & booking prefix and sequential counter for all shipments'}
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="inline-flex items-center bg-gray-100 p-1 rounded-xl font-ibm text-xs font-semibold">
          <button
            type="button"
            onClick={() => handleTabSwitch('docket')}
            className={`px-3.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-all cursor-pointer ${
              activeTab === 'docket'
                ? 'bg-white shadow-xs text-cyan-700 font-bold'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <Hash className="w-3.5 h-3.5" />
            Docket Series
          </button>
          <button
            type="button"
            onClick={() => handleTabSwitch('booking')}
            className={`px-3.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-all cursor-pointer ${
              activeTab === 'booking'
                ? 'bg-white shadow-xs text-accent-blue font-bold'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <ClipboardList className="w-3.5 h-3.5" />
            Booking Series
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          TAB 1: DOCKET NUMBER SERIES
         ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'docket' && (
        <div className="space-y-6">
          {/* Info banner */}
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <CheckCircle2 className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
            <p className="text-sm text-blue-700 font-ibm">
              All new dockets across <strong>all companies</strong> use this global series.
              Per-company series configuration has been superseded.
            </p>
          </div>

          {/* Live Preview */}
          <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="border-b-4 border-cyan-500 px-6 py-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-text-dark font-manrope">Live Preview</h3>
              {!inlineEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openInlineEdit}
                  data-testid="inline-edit-btn"
                  className="h-8 text-xs border-cyan-300 text-cyan-600 hover:bg-cyan-50"
                >
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Change
                </Button>
              )}
            </div>

            <div className="p-6">
              {!inlineEdit ? (
                <div className="flex items-center gap-4">
                  <div className="font-mono text-2xl font-bold text-accent-blue tracking-widest bg-blue-50 px-6 py-3 rounded-xl border border-blue-200">
                    {preview}
                  </div>
                  <p className="text-sm text-text-secondary font-ibm">Next docket that will be generated</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-end gap-3 flex-wrap">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-text-dark font-ibm font-medium">Prefix</Label>
                      <Input
                        data-testid="inline-prefix-input"
                        value={inlineForm.docket_prefix}
                        onChange={e => setInlineForm(p => ({ ...p, docket_prefix: e.target.value.toUpperCase() }))}
                        className="font-mono uppercase w-36 border-cyan-300 focus:border-cyan-500"
                        maxLength={10}
                        autoFocus
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-text-dark font-ibm font-medium">Separator</Label>
                      <Select value={inlineForm.separator} onValueChange={v => setInlineForm(p => ({ ...p, separator: v }))}>
                        <SelectTrigger data-testid="inline-separator-select" className="w-24 border-cyan-300 font-mono text-base font-bold">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="/">/</SelectItem>
                          <SelectItem value="-">-</SelectItem>
                          <SelectItem value="_">_</SelectItem>
                          <SelectItem value=".">.</SelectItem>
                          <SelectItem value="none">None (no separator)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-2xl font-bold text-gray-300 pb-1">→</div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-text-dark font-ibm font-medium">Next Number</Label>
                      <Input
                        data-testid="inline-next-number-input"
                        type="number"
                        min="1"
                        value={inlineForm.next_number}
                        onChange={e => setInlineForm(p => ({ ...p, next_number: parseInt(e.target.value) || 1 }))}
                        className="w-32 border-cyan-300 focus:border-cyan-500"
                      />
                    </div>
                    <div className="pb-0.5 flex items-center gap-2">
                      <div className="font-mono text-lg font-semibold text-text-secondary bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                        {`${inlineForm.docket_prefix || '?'}${toDbSep(inlineForm.separator)}${String(inlineForm.next_number || 1).padStart(form.padding_digits, '0')}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={handleQuickSave}
                      disabled={quickSaving}
                      data-testid="inline-save-btn"
                      className="h-8 bg-cyan-600 hover:bg-cyan-700 text-white"
                    >
                      {quickSaving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
                      {quickSaving ? 'Saving...' : 'Apply'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={cancelInlineEdit}
                      data-testid="inline-cancel-btn"
                      className="h-8"
                    >
                      <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Configuration */}
          <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="border-b-4 border-cyan-500 px-6 py-4">
              <h3 className="text-base font-semibold text-text-dark font-manrope">Configuration</h3>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="text-text-dark font-ibm font-medium">Docket Prefix <span className="text-red-500">*</span></Label>
                  <Input
                    data-testid="global-docket-prefix"
                    value={form.docket_prefix}
                    onChange={e => set('docket_prefix', e.target.value.toUpperCase())}
                    placeholder="e.g. STAVAN"
                    maxLength={10}
                    className="font-mono uppercase border-gray-200"
                  />
                  <p className="text-xs text-text-secondary font-ibm">Shown before the number</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-text-dark font-ibm font-medium">Separator</Label>
                  <Select value={form.separator} onValueChange={v => set('separator', v)}>
                    <SelectTrigger data-testid="global-docket-separator" className="border-gray-200 font-mono text-base font-bold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="/">/</SelectItem>
                      <SelectItem value="-">-</SelectItem>
                      <SelectItem value="_">_</SelectItem>
                      <SelectItem value=".">.</SelectItem>
                      <SelectItem value="none">None (no separator)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-text-secondary font-ibm">Between prefix and number</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-text-dark font-ibm font-medium">Next Number</Label>
                  <Input
                    data-testid="global-docket-next-number"
                    type="number"
                    min="1"
                    value={form.next_number}
                    onChange={e => set('next_number', parseInt(e.target.value) || 1)}
                    className="border-gray-200 font-ibm"
                  />
                  <p className="text-xs text-text-secondary font-ibm">Counter for the next docket</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-text-dark font-ibm font-medium">Padding Digits</Label>
                  <Input
                    data-testid="global-docket-padding"
                    type="number"
                    min="1"
                    max="10"
                    value={form.padding_digits}
                    onChange={e => set('padding_digits', parseInt(e.target.value) || 6)}
                    className="border-gray-200 font-ibm"
                  />
                  <p className="text-xs text-text-secondary font-ibm">e.g. 6 &rarr; 000001</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-text-dark font-ibm font-medium">Reset Rule</Label>
                  <Select value={form.reset_rule} onValueChange={v => set('reset_rule', v)}>
                    <SelectTrigger data-testid="global-docket-reset-rule" className="border-gray-200 font-ibm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NEVER">Never Reset (Continuous)</SelectItem>
                      <SelectItem value="FY">Financial Year (FY)</SelectItem>
                      <SelectItem value="YEARLY">Calendar Year (Jan 1)</SelectItem>
                      <SelectItem value="MONTHLY">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-text-secondary font-ibm">When the counter resets to 1</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-text-dark font-ibm font-medium">Allow Manual Printed No.</Label>
                  <Select
                    value={form.allow_manual_printed_docket_no ? 'yes' : 'no'}
                    onValueChange={v => set('allow_manual_printed_docket_no', v === 'yes')}
                  >
                    <SelectTrigger className="border-gray-200 font-ibm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes — Optional manual number</SelectItem>
                      <SelectItem value="no">No — System number only</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-text-secondary font-ibm">For pre-printed stationery</p>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                <Button
                  onClick={handleSaveDocket}
                  disabled={saving}
                  data-testid="save-global-docket-btn"
                  className="bg-cyan-600 hover:bg-cyan-700 text-white font-ibm font-medium px-6"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {saving ? 'Saving...' : 'Save Docket Settings'}
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleInitialize}
                  disabled={initializing}
                  data-testid="init-global-docket-btn"
                  className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  {initializing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                  {initializing ? 'Syncing...' : 'Sync from Existing Dockets'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          TAB 2: BOOKING NUMBER SERIES
         ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'booking' && (
        <div className="space-y-6">
          {/* Info banner */}
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <CheckCircle2 className="w-5 h-5 text-accent-blue mt-0.5 shrink-0" />
            <p className="text-sm text-blue-700 font-ibm">
              All new booking inquiries generated in the <strong>Admin Panel</strong>, <strong>Customer Portal</strong>, and <strong>Website Booking Form</strong> will use this sequential series.
            </p>
          </div>

          {/* Live Preview */}
          <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="border-b-4 border-accent-blue px-6 py-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-text-dark font-manrope">Live Preview</h3>
              {!bkInlineEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openBkInlineEdit}
                  data-testid="bk-inline-edit-btn"
                  className="h-8 text-xs border-blue-300 text-accent-blue hover:bg-blue-50"
                >
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Quick Change
                </Button>
              )}
            </div>

            <div className="p-6">
              {!bkInlineEdit ? (
                <div className="flex items-center gap-4">
                  <div className="font-mono text-2xl font-bold text-accent-blue tracking-widest bg-blue-50 px-6 py-3 rounded-xl border border-blue-200">
                    {bkPreview}
                  </div>
                  <p className="text-sm text-text-secondary font-ibm">Next booking inquiry number that will be issued</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-end gap-3 flex-wrap">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-text-dark font-ibm font-medium">Prefix</Label>
                      <Input
                        value={bkInlineForm.booking_prefix}
                        onChange={e => setBkInlineForm(p => ({ ...p, booking_prefix: e.target.value.toUpperCase() }))}
                        className="font-mono uppercase w-36 border-blue-300 focus:border-blue-500"
                        maxLength={10}
                        autoFocus
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-text-dark font-ibm font-medium">Separator</Label>
                      <Select value={bkInlineForm.separator} onValueChange={v => setBkInlineForm(p => ({ ...p, separator: v }))}>
                        <SelectTrigger className="w-24 border-blue-300 font-mono text-base font-bold">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="-">-</SelectItem>
                          <SelectItem value="/">/</SelectItem>
                          <SelectItem value="_">_</SelectItem>
                          <SelectItem value=".">.</SelectItem>
                          <SelectItem value="none">None</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-2xl font-bold text-gray-300 pb-1">→</div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-text-dark font-ibm font-medium">Next Number</Label>
                      <Input
                        type="number"
                        min="1"
                        value={bkInlineForm.next_number}
                        onChange={e => setBkInlineForm(p => ({ ...p, next_number: parseInt(e.target.value) || 1 }))}
                        className="w-32 border-blue-300 focus:border-blue-500"
                      />
                    </div>
                    <div className="pb-0.5 flex items-center gap-2">
                      <div className="font-mono text-lg font-semibold text-text-secondary bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                        {`${bkInlineForm.booking_prefix || '?'}${toDbSep(bkInlineForm.separator)}${String(bkInlineForm.next_number || 1).padStart(bkForm.padding_digits, '0')}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={handleBkQuickSave}
                      disabled={bkQuickSaving}
                      className="h-8 bg-accent-blue hover:bg-accent-blue/90 text-white"
                    >
                      {bkQuickSaving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
                      {bkQuickSaving ? 'Saving...' : 'Apply'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={cancelBkInlineEdit}
                      className="h-8"
                    >
                      <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Configuration */}
          <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="border-b-4 border-accent-blue px-6 py-4">
              <h3 className="text-base font-semibold text-text-dark font-manrope">Booking Series Configuration</h3>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Prefix */}
                <div className="space-y-2">
                  <Label className="text-text-dark font-ibm font-medium">Booking Prefix <span className="text-red-500">*</span></Label>
                  <Input
                    data-testid="booking-prefix-input"
                    value={bkForm.booking_prefix}
                    onChange={e => setBk('booking_prefix', e.target.value.toUpperCase())}
                    placeholder="e.g. INQ or BK"
                    maxLength={10}
                    className="font-mono uppercase border-gray-200"
                  />
                  <p className="text-xs text-text-secondary font-ibm">Prefix (e.g. INQ, BK, REQ)</p>
                </div>

                {/* Separator */}
                <div className="space-y-2">
                  <Label className="text-text-dark font-ibm font-medium">Separator</Label>
                  <Select value={bkForm.separator} onValueChange={v => setBk('separator', v)}>
                    <SelectTrigger className="border-gray-200 font-mono text-base font-bold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-">-</SelectItem>
                      <SelectItem value="/">/</SelectItem>
                      <SelectItem value="_">_</SelectItem>
                      <SelectItem value=".">.</SelectItem>
                      <SelectItem value="none">None (no separator)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-text-secondary font-ibm">Between prefix and counter</p>
                </div>

                {/* Next Number */}
                <div className="space-y-2">
                  <Label className="text-text-dark font-ibm font-medium">Next Number</Label>
                  <Input
                    data-testid="booking-next-number-input"
                    type="number"
                    min="1"
                    value={bkForm.next_number}
                    onChange={e => setBk('next_number', parseInt(e.target.value) || 1)}
                    className="border-gray-200 font-ibm"
                  />
                  <p className="text-xs text-text-secondary font-ibm">Counter for the next inquiry</p>
                </div>

                {/* Padding */}
                <div className="space-y-2">
                  <Label className="text-text-dark font-ibm font-medium">Padding Digits</Label>
                  <Input
                    data-testid="booking-padding-input"
                    type="number"
                    min="1"
                    max="10"
                    value={bkForm.padding_digits}
                    onChange={e => setBk('padding_digits', parseInt(e.target.value) || 4)}
                    className="border-gray-200 font-ibm"
                  />
                  <p className="text-xs text-text-secondary font-ibm">e.g. 4 &rarr; 0001, 6 &rarr; 000001</p>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                <Button
                  onClick={handleSaveBooking}
                  disabled={bkSaving}
                  data-testid="save-booking-series-btn"
                  className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-medium px-6"
                >
                  {bkSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {bkSaving ? 'Saving...' : 'Save Booking Series'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GlobalDocketSettings;
